"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@multi-tenant-cms/ui/components/button";
import { Checkbox } from "@multi-tenant-cms/ui/components/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@multi-tenant-cms/ui/components/dialog";
import { Label } from "@multi-tenant-cms/ui/components/label";
import { cn } from "@multi-tenant-cms/ui/lib/utils";
import { CookieIcon, Settings2Icon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { CookieConsentCategorySelection, CookieConsentConfig, OptionalCookieConsentCategoryKey } from "../../../types/src/seo";
import { CookieConsentShadowHost } from "./cookie-consent-shadow-host";
const OPTIONAL_CATEGORIES: readonly OptionalCookieConsentCategoryKey[] = ["preferences", "analytics", "marketing"];
type CookieConsentSurfaceProps = {
  config: CookieConsentConfig;
  onAcceptAll?: () => void;
  onRejectAll?: () => void;
  onSavePreferences?: (nextSelection: CookieConsentCategorySelection) => void;
  previewMode?: boolean;
  selection: CookieConsentCategorySelection;
  showBanner: boolean;
};
function bannerPlacementClass(position: CookieConsentConfig["banner_position"], previewMode: boolean) {
  const base = previewMode ? "absolute inset-x-4 bottom-4" : "fixed z-50 inset-x-4 bottom-4";
  if (position === "bottom-left") {
    return cn(base, "mx-0 max-w-md md:right-auto md:left-4");
  }
  if (position === "bottom-center") {
    return cn(base, "mx-auto max-w-2xl");
  }
  return cn(base, "mx-0 max-w-md md:right-4 md:left-auto");
}
function manageButtonPlacementClass(position: CookieConsentConfig["banner_position"], previewMode: boolean) {
  const base = previewMode ? "absolute bottom-4" : "fixed bottom-4 z-40";
  if (position === "bottom-left") {
    return cn(base, "right-4");
  }
  if (position === "bottom-center") {
    return cn(base, "right-4");
  }
  return cn(base, "left-4");
}
function CookieConsentSurfaceView(t0) {
  const $ = _c(114);
  const {
    config,
    onAcceptAll,
    onRejectAll,
    onSavePreferences,
    previewMode: t1,
    selection,
    showBanner
  } = t0;
  const previewMode = t1 === undefined ? false : t1;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPortal, setDialogPortal] = useState(null);
  const [draftSelection, setDraftSelection] = useState(selection);
  let t2;
  let t3;
  if ($[0] !== selection) {
    t2 = () => {
      setDraftSelection(selection);
    };
    t3 = [selection];
    $[0] = selection;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  if (!config.enabled) {
    return null;
  }
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== config.accept_all_label || $[4] !== config.banner_description || $[5] !== config.banner_position || $[6] !== config.banner_title || $[7] !== config.categories || $[8] !== config.cookie_policy_url || $[9] !== config.customize_label || $[10] !== config.manage_preferences_label || $[11] !== config.preferences_description || $[12] !== config.preferences_title || $[13] !== config.privacy_policy_url || $[14] !== config.reject_all_label || $[15] !== dialogOpen || $[16] !== dialogPortal || $[17] !== draftSelection || $[18] !== onAcceptAll || $[19] !== onRejectAll || $[20] !== previewMode || $[21] !== showBanner) {
    let t15;
    if ($[35] !== config.categories) {
      t15 = category => config.categories[category].enabled;
      $[35] = config.categories;
      $[36] = t15;
    } else {
      t15 = $[36];
    }
    const categoryRows = OPTIONAL_CATEGORIES.filter(t15);
    const shouldShowManageButton = !showBanner;
    const hasPolicyLinks = Boolean(config.privacy_policy_url || config.cookie_policy_url);
    if ($[37] !== config.banner_position || $[38] !== config.manage_preferences_label || $[39] !== previewMode || $[40] !== shouldShowManageButton) {
      t12 = shouldShowManageButton ? <div className={manageButtonPlacementClass(config.banner_position, previewMode)} style={{
        pointerEvents: "auto"
      }}><Button onClick={() => setDialogOpen(true)} size="sm" type="button" variant="outline"><Settings2Icon className="size-4" />{config.manage_preferences_label}</Button></div> : null;
      $[37] = config.banner_position;
      $[38] = config.manage_preferences_label;
      $[39] = previewMode;
      $[40] = shouldShowManageButton;
      $[41] = t12;
    } else {
      t12 = $[41];
    }
    if ($[42] !== config.accept_all_label || $[43] !== config.banner_description || $[44] !== config.banner_position || $[45] !== config.banner_title || $[46] !== config.cookie_policy_url || $[47] !== config.customize_label || $[48] !== config.privacy_policy_url || $[49] !== config.reject_all_label || $[50] !== hasPolicyLinks || $[51] !== onAcceptAll || $[52] !== onRejectAll || $[53] !== previewMode || $[54] !== showBanner) {
      t13 = showBanner ? <div className={bannerPlacementClass(config.banner_position, previewMode)} style={{
        pointerEvents: "auto"
      }}><div className="rounded-xl border border-border bg-background/95 p-4 shadow-2xl backdrop-blur"><div className="flex items-start gap-3"><div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary"><CookieIcon className="size-4" /></div><div className="min-w-0 flex-1 space-y-2"><div><p className="font-semibold text-foreground text-sm">{config.banner_title}</p><p className="mt-1 text-muted-foreground text-sm">{config.banner_description}</p></div>{hasPolicyLinks ? <div className="flex flex-wrap gap-3 text-sm">{config.privacy_policy_url ? <a className="text-primary underline underline-offset-4" href={config.privacy_policy_url} rel="noreferrer" target="_blank">Privacy policy</a> : null}{config.cookie_policy_url ? <a className="text-primary underline underline-offset-4" href={config.cookie_policy_url} rel="noreferrer" target="_blank">Cookie policy</a> : null}</div> : null}<div className="flex flex-wrap gap-2"><Button onClick={onRejectAll} size="sm" type="button" variant="outline">{config.reject_all_label}</Button><Button onClick={() => setDialogOpen(true)} size="sm" type="button" variant="secondary">{config.customize_label}</Button><Button onClick={onAcceptAll} size="sm" type="button">{config.accept_all_label}</Button></div></div></div></div></div> : null;
      $[42] = config.accept_all_label;
      $[43] = config.banner_description;
      $[44] = config.banner_position;
      $[45] = config.banner_title;
      $[46] = config.cookie_policy_url;
      $[47] = config.customize_label;
      $[48] = config.privacy_policy_url;
      $[49] = config.reject_all_label;
      $[50] = hasPolicyLinks;
      $[51] = onAcceptAll;
      $[52] = onRejectAll;
      $[53] = previewMode;
      $[54] = showBanner;
      $[55] = t13;
    } else {
      t13 = $[55];
    }
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <div ref={setDialogPortal} style={{
        display: "contents"
      }} />;
      $[56] = t14;
    } else {
      t14 = $[56];
    }
    T1 = Dialog;
    t10 = setDialogOpen;
    t11 = dialogOpen;
    T0 = DialogContent;
    t7 = "max-w-2xl";
    t8 = previewMode ? undefined : dialogPortal;
    let t16;
    if ($[57] !== config.preferences_title) {
      t16 = <DialogTitle>{config.preferences_title}</DialogTitle>;
      $[57] = config.preferences_title;
      $[58] = t16;
    } else {
      t16 = $[58];
    }
    let t17;
    if ($[59] !== config.preferences_description) {
      t17 = <DialogDescription>{config.preferences_description}</DialogDescription>;
      $[59] = config.preferences_description;
      $[60] = t17;
    } else {
      t17 = $[60];
    }
    if ($[61] !== t16 || $[62] !== t17) {
      t9 = <DialogHeader>{t16}{t17}</DialogHeader>;
      $[61] = t16;
      $[62] = t17;
      $[63] = t9;
    } else {
      t9 = $[63];
    }
    t4 = "space-y-4";
    let t18;
    if ($[64] !== config.categories.essential.label) {
      t18 = <p className="font-medium text-sm">{config.categories.essential.label}</p>;
      $[64] = config.categories.essential.label;
      $[65] = t18;
    } else {
      t18 = $[65];
    }
    let t19;
    if ($[66] !== config.categories.essential.description) {
      t19 = <p className="text-muted-foreground text-sm">{config.categories.essential.description}</p>;
      $[66] = config.categories.essential.description;
      $[67] = t19;
    } else {
      t19 = $[67];
    }
    let t20;
    if ($[68] !== t18 || $[69] !== t19) {
      t20 = <div className="space-y-1">{t18}{t19}</div>;
      $[68] = t18;
      $[69] = t19;
      $[70] = t20;
    } else {
      t20 = $[70];
    }
    let t21;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <div className="flex items-center gap-2"><Checkbox checked={true} disabled={true} /><span className="text-muted-foreground text-sm">Always on</span></div>;
      $[71] = t21;
    } else {
      t21 = $[71];
    }
    if ($[72] !== t20) {
      t5 = <div className="rounded-lg border border-border bg-muted/30 p-4"><div className="flex items-start justify-between gap-4">{t20}{t21}</div></div>;
      $[72] = t20;
      $[73] = t5;
    } else {
      t5 = $[73];
    }
    let t22;
    if ($[74] !== config.categories || $[75] !== draftSelection) {
      t22 = category_0 => <div className="rounded-lg border border-border p-4" key={category_0}><div className="flex items-start justify-between gap-4"><div className="space-y-1"><Label className="font-medium text-sm" htmlFor={`cookie-category-${category_0}`}>{config.categories[category_0].label}</Label><p className="text-muted-foreground text-sm">{config.categories[category_0].description}</p></div><Checkbox checked={draftSelection[category_0]} id={`cookie-category-${category_0}`} onCheckedChange={checked => setDraftSelection(current => ({
            ...current,
            [category_0]: checked === true
          }))} /></div></div>;
      $[74] = config.categories;
      $[75] = draftSelection;
      $[76] = t22;
    } else {
      t22 = $[76];
    }
    t6 = categoryRows.map(t22);
    $[3] = config.accept_all_label;
    $[4] = config.banner_description;
    $[5] = config.banner_position;
    $[6] = config.banner_title;
    $[7] = config.categories;
    $[8] = config.cookie_policy_url;
    $[9] = config.customize_label;
    $[10] = config.manage_preferences_label;
    $[11] = config.preferences_description;
    $[12] = config.preferences_title;
    $[13] = config.privacy_policy_url;
    $[14] = config.reject_all_label;
    $[15] = dialogOpen;
    $[16] = dialogPortal;
    $[17] = draftSelection;
    $[18] = onAcceptAll;
    $[19] = onRejectAll;
    $[20] = previewMode;
    $[21] = showBanner;
    $[22] = T0;
    $[23] = T1;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    T0 = $[22];
    T1 = $[23];
    t10 = $[24];
    t11 = $[25];
    t12 = $[26];
    t13 = $[27];
    t14 = $[28];
    t4 = $[29];
    t5 = $[30];
    t6 = $[31];
    t7 = $[32];
    t8 = $[33];
    t9 = $[34];
  }
  let t15;
  if ($[77] !== t4 || $[78] !== t5 || $[79] !== t6) {
    t15 = <div className={t4}>{t5}{t6}</div>;
    $[77] = t4;
    $[78] = t5;
    $[79] = t6;
    $[80] = t15;
  } else {
    t15 = $[80];
  }
  let t16;
  if ($[81] !== config.privacy_policy_url) {
    t16 = config.privacy_policy_url ? <a className="text-primary underline underline-offset-4" href={config.privacy_policy_url} rel="noreferrer" target="_blank">Privacy policy</a> : null;
    $[81] = config.privacy_policy_url;
    $[82] = t16;
  } else {
    t16 = $[82];
  }
  let t17;
  if ($[83] !== config.cookie_policy_url) {
    t17 = config.cookie_policy_url ? <a className="text-primary underline underline-offset-4" href={config.cookie_policy_url} rel="noreferrer" target="_blank">Cookie policy</a> : null;
    $[83] = config.cookie_policy_url;
    $[84] = t17;
  } else {
    t17 = $[84];
  }
  let t18;
  if ($[85] !== t16 || $[86] !== t17) {
    t18 = <div className="flex flex-wrap gap-3 text-sm">{t16}{t17}</div>;
    $[85] = t16;
    $[86] = t17;
    $[87] = t18;
  } else {
    t18 = $[87];
  }
  let t19;
  if ($[88] !== draftSelection || $[89] !== onSavePreferences) {
    t19 = () => {
      onSavePreferences?.({
        ...draftSelection,
        essential: true
      });
      setDialogOpen(false);
    };
    $[88] = draftSelection;
    $[89] = onSavePreferences;
    $[90] = t19;
  } else {
    t19 = $[90];
  }
  let t20;
  if ($[91] !== config.save_preferences_label || $[92] !== t19) {
    t20 = <Button onClick={t19} type="button">{config.save_preferences_label}</Button>;
    $[91] = config.save_preferences_label;
    $[92] = t19;
    $[93] = t20;
  } else {
    t20 = $[93];
  }
  let t21;
  if ($[94] !== t18 || $[95] !== t20) {
    t21 = <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">{t18}{t20}</DialogFooter>;
    $[94] = t18;
    $[95] = t20;
    $[96] = t21;
  } else {
    t21 = $[96];
  }
  let t22;
  if ($[97] !== T0 || $[98] !== t15 || $[99] !== t21 || $[100] !== t7 || $[101] !== t8 || $[102] !== t9) {
    t22 = <T0 className={t7} portalContainer={t8}>{t9}{t15}{t21}</T0>;
    $[97] = T0;
    $[98] = t15;
    $[99] = t21;
    $[100] = t7;
    $[101] = t8;
    $[102] = t9;
    $[103] = t22;
  } else {
    t22 = $[103];
  }
  let t23;
  if ($[104] !== T1 || $[105] !== t10 || $[106] !== t11 || $[107] !== t22) {
    t23 = <T1 onOpenChange={t10} open={t11}>{t22}</T1>;
    $[104] = T1;
    $[105] = t10;
    $[106] = t11;
    $[107] = t22;
    $[108] = t23;
  } else {
    t23 = $[108];
  }
  let t24;
  if ($[109] !== t12 || $[110] !== t13 || $[111] !== t14 || $[112] !== t23) {
    t24 = <>{t12}{t13}{t14}{t23}</>;
    $[109] = t12;
    $[110] = t13;
    $[111] = t14;
    $[112] = t23;
    $[113] = t24;
  } else {
    t24 = $[113];
  }
  return t24;
}
export function CookieConsentSurface(t0) {
  const $ = _c(17);
  const {
    config,
    onAcceptAll,
    onRejectAll,
    onSavePreferences,
    previewMode: t1,
    selection,
    showBanner
  } = t0;
  const previewMode = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== config || $[1] !== onAcceptAll || $[2] !== onRejectAll || $[3] !== onSavePreferences || $[4] !== previewMode || $[5] !== selection || $[6] !== showBanner) {
    t2 = () => <CookieConsentSurfaceView config={config} onAcceptAll={onAcceptAll} onRejectAll={onRejectAll} onSavePreferences={onSavePreferences} previewMode={previewMode} selection={selection} showBanner={showBanner} />;
    $[0] = config;
    $[1] = onAcceptAll;
    $[2] = onRejectAll;
    $[3] = onSavePreferences;
    $[4] = previewMode;
    $[5] = selection;
    $[6] = showBanner;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const renderSurface = t2;
  if (!config.enabled) {
    return null;
  }
  if (previewMode) {
    let t3;
    if ($[8] !== config || $[9] !== onAcceptAll || $[10] !== onRejectAll || $[11] !== onSavePreferences || $[12] !== selection || $[13] !== showBanner) {
      t3 = <CookieConsentSurfaceView config={config} onAcceptAll={onAcceptAll} onRejectAll={onRejectAll} onSavePreferences={onSavePreferences} previewMode={true} selection={selection} showBanner={showBanner} />;
      $[8] = config;
      $[9] = onAcceptAll;
      $[10] = onRejectAll;
      $[11] = onSavePreferences;
      $[12] = selection;
      $[13] = showBanner;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    return t3;
  }
  let t3;
  if ($[15] !== renderSurface) {
    t3 = <CookieConsentShadowHost previewMode={false} render={renderSurface} />;
    $[15] = renderSurface;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}